<script setup>
  import { ref } from "vue";
  import { Button } from "@/components/ui/button";
  import { Tooltip } from "@/components/ui/tooltip";
  import { Pencil, Trash2 } from "lucide-vue-next";
  import CommentComposer from "./CommentComposer.vue";
  import MentionText from "./MentionText.vue";
  import { relativeTimeFrom, shortDateTimeFrom } from "@utils/time";
  import { useReviewCommentsStore } from "@stores/survey/reviewComments";

  const props = defineProps({
    thread: { type: Object, required: true },
    comment: { type: Object, required: true },
    withMeta: { type: Boolean, default: false },
  });

  const store = useReviewCommentsStore();
  const editing = ref(false);

  const save = async (body, mentions) => {
    await store.updateComment(props.thread, props.comment, body, mentions);
    editing.value = false;
  };
</script>

<template>
  <div class="group/comment relative">
    <div class="min-w-0">
      <div
        v-if="withMeta"
        class="text-xs text-muted-foreground">
        <span class="font-semibold text-foreground">{{ comment.user.full_name }}</span>
        <span
          v-if="comment.posted_via"
          class="italic">
          via {{ comment.posted_via }}</span
        >
        · {{ relativeTimeFrom(comment.created_at) }}
        <Tooltip
          v-if="comment.edited_at"
          :content="`Edited ${shortDateTimeFrom(comment.edited_at)}`">
          <span>· edited</span>
        </Tooltip>
      </div>
      <p
        v-if="comment.deleted"
        class="text-13 italic text-muted-foreground">
        Deleted comment
      </p>
      <CommentComposer
        v-else-if="editing"
        mode="edit"
        autofocus
        :initial-body="comment.body"
        @click.stop
        @submit="save"
        @cancel="editing = false" />
      <p
        v-else
        class="text-13 whitespace-pre-wrap break-words leading-relaxed">
        <MentionText :body="comment.body" />
      </p>
    </div>
    <div
      v-if="store.isMine(comment) && !comment.deleted && !editing"
      class="absolute -top-1 right-0 flex rounded-md bg-card opacity-0 shadow-xs group-hover/comment:opacity-100">
      <Button
        variant="ghost"
        size="icon"
        class="size-6 text-muted-foreground hover:text-primary"
        aria-label="Edit comment"
        @click.stop="editing = true">
        <Pencil class="size-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        class="size-6 text-muted-foreground hover:text-status-error-strong"
        aria-label="Delete comment"
        @click.stop="store.deleteComment(thread, comment)">
        <Trash2 class="size-3.5" />
      </Button>
    </div>
  </div>
</template>
